import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    Button, Col, Input,
    Label,
    Row,
    Spinner
} from 'reactstrap';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { NetworkConceptsFilter } from '../../redux/AccountsSlice';
import {
    Afr, AuditMessage, ValidationError, commitAfr, getSelectedAfr, getVersionPropertiesChanged, isAFRAuditRequired,
    isFundGroupOriented,
    loadAfr, selectAfrStateStatus,
    thirdPartyNotifyLGEs
} from '../../redux/AfrSlice';
import { resetConceptState } from "../../redux/ConceptSlice";
import { Entity, getSelectedEntity } from '../../redux/EntitySlice';
import { loadEvents } from '../../redux/EventsSlice';
import { getReportNotify, getSelector, getTaxonomyType, isAdmin, isDirty, setDimensionSectionMap, setIsDirty, setIsLoading } from '../../redux/MiscSlice';
import { Network, loadDefinitionNetworks, loadScopeNetworks, resetNetworkState } from '../../redux/NetworkSlice';
import { PartInput, ReferenceAssignmentInput, ReferenceInput, TaxonomyUpdate, TaxonomyUpdateReport, immediateUpdate } from '../../redux/TaxonomyChangesSlice';
import { Part, Reference, loadTaxonomyReference } from '../../redux/TaxonomyReferenceSlice';
import { ActiveTaxonomyVersionInfo, TaxonomyTypeEnum, VersionRef, getTaxonomyReferenceForAfr } from '../../redux/TaxonomyVersionSlice';
import { loadTitles, resetTitleState } from "../../redux/TitleSlice";
import { AdminUserRoleEnum, EntityAuthorization, UserRoleEnum, selectAdminUserRole, selectUser, selectUserEntityAuthorizations, selectUserLogged } from '../../redux/UserSlice';
import { ErrorGrid, getErrorList, getSupplementalGridErrorList, getTotalErrorCount } from '../../redux/ValidationSlice';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import AfrSubmissionSurvey from '../layout/Modal/AfrSubmissionSurvey';
import NavigatorIconNotification from './NavigatorIconNotification';

export enum NavOptionTypeEnum {
    INFO = 'Information',
    SUBMIT = 'Final Step',
    WORK = 'Incomplete',
    COMPLETE = 'Completed'
}



export interface NavOption {
    title: string;
    route: string;
    section: string;
    conceptName: string;
    optionType: NavOptionTypeEnum;
}
export interface NavOptionState {
    option: NavOption;
}
export interface NavOptionGroup {
    options: NavOptionState[];
    group: NavOptionTypeEnum;
}
const AfrNavigator = (props: any) => {
    const dispatch = useAppDispatch();
    const MySwal = withReactContent(Swal);
    var location = useLocation();
    const history = useHistory();
    const selected: Afr | null = useAppSelector(getSelectedAfr);
    const selectedLoadAfrStatus = useAppSelector(selectAfrStateStatus);
    const selectedEntity: Entity | null = useAppSelector(getSelectedEntity);
    const isAdminUser: boolean = useAppSelector(isAdmin);
    const authorizedEntitiesSelector: Array<EntityAuthorization> | null = useAppSelector(selectUserEntityAuthorizations);
    const stateChanges = useAppSelector(getVersionPropertiesChanged);
    const errorList = useAppSelector(getErrorList);
    const rowErrorList: Array<ErrorGrid> = useAppSelector(getSupplementalGridErrorList);
    const isDirtyInfo: boolean = useAppSelector(isDirty);
    const selectedId = selected ? selected.afrId : "";
    const user = useAppSelector(selectUser);
    const loggedUser = useAppSelector(selectUserLogged);
    const userRole = (user && user.userProfile) ? user.userProfile.role : UserRoleEnum.EntityUser.toString();
    var navOptions: NavOption[] = [];
    var initialLocation = "";
    var tType: TaxonomyTypeEnum | null = useAppSelector(getTaxonomyType);
    const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);
    const [saveButtonTitle, setSaveButtonTitle] = useState("Save");
    const [submitButtonTitle, setSubmitButtonTitle] = useState("Submit");
    const [isNotThirdParty, setIsNotThirdParty] = useState<boolean>(true);
    const [modalSurveyIsOpen, setModalSurveyIsOpen] = useState(false);
    const [protoSurvey, setProtoSurvey] = useState<Reference | null>(null);
    const handleCloseSurveyModal = function () {
        setModalSurveyIsOpen(false);
    }
    const openInstructionsModal = function () {
        setModalSurveyIsOpen(true);
    }

    const totalErrorCount: any = useAppSelector(getTotalErrorCount);
    const mustReportDiscreteComponentUnits = function (afr: Afr | null): boolean {
        if (!afr || !afr.affiliateReporting || afr.affiliateReporting.length === 0) {
            return false;
        }
        //        var discrep: AffiliateReporting | undefined = afr.affiliateReporting.find(rep => rep.reportingType === "DISCRETE");
        //        return discrep !== undefined;
        // After discussion with A&A determined that the simplest behavior would be preferable - the component units section will appear if the entity has affiliates,
        // regardless of the reporting state of the affiliates. There is an exsiting validation rule to flag the use of component unit facts when there are no discrete afiiliates,
        // so trying to dynaically tie the presence of the component units section to the detailed state of affiliate reporting is not necessary and leads to complications ragarding "stranded" 
        // facts when the user changes reporting type.
        return true;
    }

    var sectionCode = "";
    if (errorList && errorList.length > 0) {
        errorList.map((i:any) => {
            sectionCode = i.section.sectionCode;
        });    
    }

    if (userRole === UserRoleEnum.Admin) {
        switch (tType) {
            case TaxonomyTypeEnum.COURT_REPORTING_939:
                initialLocation = "/CourtReporting939AdminA";
                break;

            case TaxonomyTypeEnum.COURT_REPORTING_29:
                initialLocation = "/CR29AdminA";
                break;

            default:
                initialLocation = "/AfrSummaryA";
                break;
        }
        if (selected && (selected.status === "REPORTED_BY_PRIMARY")) {
            if (selected.audittype === "AUDIT") {
                navOptions = [
                    { title: "Summary", route: "/AfrSummaryA", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                    { title: "Audit", route: "/AuditA", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK }
                ]
            } else {
                navOptions = [
                    { title: "Summary", route: "/AfrSummaryA", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO }
                ]
            }
        } else {
            switch (tType) {
                case TaxonomyTypeEnum.COURT_REPORTING_939:
                    navOptions = [
                        { title: "Admin", route: "/CourtReporting939AdminA", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        // NOTE - Importing done in documents part of admin                        { title: "Import 939 Data", route: "/CourtReporting939ImportExportA", section: "20", conceptName: "" },
                        { title: "Court Innovations", route: "/CourtReporting939CIA", section: enums.UISection_CR939.courtInnovations, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                        { title: "Legal Aid Program", route: "/CourtReporting939LAPA", section: enums.UISection_CR939.legalAidProgram, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                        { title: "Law Library", route: "/CourtReporting939LLA", section: enums.UISection_CR939.lawLibrary, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                        { title: "Juvenile Alternative Program", route: "/CourtReporting939JAPA", section: enums.UISection_CR939.juvenileAlternativeProgram, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                        { title: "Summary", route: "/CourtReporting939SummaryA", section: enums.UISection_CR939.dataSummary, conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        { title: "Reports", route: "/CR939ReportsA", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        { title: "Errors", route: "/CR939ErrorsA", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                    ]
                    break;

                case TaxonomyTypeEnum.COURT_REPORTING_29:

                    if (sectionCode == "99") {
                        navOptions = [
                            { title: "Admin", route: "/CR29AdminA", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            // NOTE - Importing done in documents part of admin                        { title: "Import 939 Data", route: "/CourtReporting939ImportExportA", section: "20", conceptName: "" },
                            { title: "Part A", route: "/CR29DataAA", section: enums.UISection_CR29.CR29PartA, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part B", route: "/CR29DataBA", section: enums.UISection_CR29.CR29PartB, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part C", route: "/CR29DataCA", section: enums.UISection_CR29.CR29PartC, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part D", route: "/CR29DataDA", section: enums.UISection_CR29.CR29PartD, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part E", route: "/CR29DataEA", section: enums.UISection_CR29.CR29PartE, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Certification", route: "/CR29CertificationA", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                            { title: "Reports", route: "/CR29ReportsA", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Errors", route: "/CR29ErrorsA", section: "99", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Instructions", route: "/InstructionsA", section: enums.UISection_CR29.instructions, conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        ]
                    }
                    else {
                        navOptions = [
                            { title: "Admin", route: "/CR29AdminA", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            // NOTE - Importing done in documents part of admin                        { title: "Import 939 Data", route: "/CourtReporting939ImportExportA", section: "20", conceptName: "" },
                            { title: "Part A", route: "/CR29DataAA", section: enums.UISection_CR29.CR29PartA, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part B", route: "/CR29DataBA", section: enums.UISection_CR29.CR29PartB, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part C", route: "/CR29DataCA", section: enums.UISection_CR29.CR29PartC, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part D", route: "/CR29DataDA", section: enums.UISection_CR29.CR29PartD, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part E", route: "/CR29DataEA", section: enums.UISection_CR29.CR29PartE, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Certification", route: "/CR29CertificationA", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                            { title: "Reports", route: "/CR29ReportsA", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Errors", route: "/CR29ErrorsA", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Instructions", route: "/InstructionsA", section: enums.UISection_CR29.instructions, conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        ]
                    }
                    break;

                default:
                    var afrYearA: number = selected ? selected.year : 2019
                    if (afrYearA > 2023) {
                        if (mustReportDiscreteComponentUnits(selected)) {
                            navOptions = [
                                { title: "Summary", route: "/AfrSummaryA", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: "Import AFR Data", route: "/AfrDataImportA", section: "02", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: utilityHelper.getFundGroupTitle("Governmental"), route: "/AccountsByGovernmentalA", section: enums.UISection_FundGroup.governmentalFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: utilityHelper.getFundGroupTitle("Proprietary"), route: "/AccountsByProprietaryA", section: enums.UISection_FundGroup.proprietaryFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: utilityHelper.getFundGroupTitle("Fiduciary"), route: "/AccountsByFiduciaryA", section: enums.UISection_FundGroup.fiduciaryFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: utilityHelper.getFundGroupTitle("Component Units"), route: "/AccountsByComponentUnitsA", section: enums.UISection_FundGroup.componentUnitsFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Long-Term Debt", route: "/LongTermDebtA", section: "06", conceptName: "UAS_005_LongTermDebtTitle", optionType: NavOptionTypeEnum.WORK },
                                { title: "Supplemental Information", route: "/AfrDataA", section: "07", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Affiliated Entities", route: "/AffiliatedEntitiesA", section: "08", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Audit", route: "/AuditA", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Certification", route: "/CertificationA", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                                { title: "Reports", route: "/AfrReportsA", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: "Errors", route: "/ErrorsA", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            ]
                        } else {
                            navOptions = [
                                { title: "Summary", route: "/AfrSummaryA", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: "Import AFR Data", route: "/AfrDataImportA", section: "02", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: utilityHelper.getFundGroupTitle("Governmental"), route: "/AccountsByGovernmentalA", section: enums.UISection_FundGroup.governmentalFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: utilityHelper.getFundGroupTitle("Proprietary"), route: "/AccountsByProprietaryA", section: enums.UISection_FundGroup.proprietaryFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: utilityHelper.getFundGroupTitle("Fiduciary"), route: "/AccountsByFiduciaryA", section: enums.UISection_FundGroup.fiduciaryFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Long-Term Debt", route: "/LongTermDebtA", section: "06", conceptName: "UAS_005_LongTermDebtTitle", optionType: NavOptionTypeEnum.WORK },
                                { title: "Supplemental Information", route: "/AfrDataA", section: "07", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Affiliated Entities", route: "/AffiliatedEntitiesA", section: "08", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Audit", route: "/AuditA", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Certification", route: "/CertificationA", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                                { title: "Reports", route: "/AfrReportsA", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: "Errors", route: "/ErrorsA", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            ]
                        }
                    } else if (afrYearA > 2021) {
                        navOptions = [
                            { title: "Summary", route: "/AfrSummaryA", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Import AFR Data", route: "/AfrDataImportA", section: "02", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Balance Sheet", route: "/BalanceSheetA", section: "03", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Revenues", route: "/RevenuesA", section: "04", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Expenditures", route: "/ExpendituresA", section: "05", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Long-Term Debt", route: "/LongTermDebtA", section: "06", conceptName: "UAS_005_LongTermDebtTitle", optionType: NavOptionTypeEnum.WORK },
                            { title: "Supplemental Information", route: "/AfrDataA", section: "07", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Affiliated Entities", route: "/AffiliatedEntitiesA", section: "08", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Audit", route: "/AuditA", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Certification", route: "/CertificationA", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                            { title: "Reports", route: "/AfrReportsA", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Errors", route: "/ErrorsA", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        ]
                    } else if (afrYearA > 2020) {
                        navOptions = [
                            { title: "Summary", route: "/AfrSummaryA", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Import AFR Data", route: "/AfrDataImportA", section: "02", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Revenues", route: "/RevenuesA", section: "04", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Expenditures", route: "/ExpendituresA", section: "05", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Long-Term Debt", route: "/LongTermDebtA", section: "06", conceptName: "UAS_005_LongTermDebtTitle", optionType: NavOptionTypeEnum.WORK },
                            { title: "Supplemental Information", route: "/AfrDataA", section: "07", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Affiliated Entities", route: "/AffiliatedEntitiesA", section: "08", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Audit", route: "/AuditA", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Certification", route: "/CertificationA", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                            { title: "Reports", route: "/AfrReportsA", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Errors", route: "/ErrorsA", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        ]
                    } else {
                        navOptions = [
                            { title: "Summary", route: "/AfrSummaryA", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Import AFR Data", route: "/AfrDataImportA", section: "02", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Revenues", route: "/RevenuesA", section: "04", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Expenditures", route: "/ExpendituresA", section: "05", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Long-Term Debt", route: "/LongTermDebtA", section: "06", conceptName: "UAS_005_LongTermDebtTitle", optionType: NavOptionTypeEnum.WORK },
                            { title: "Affiliated Entities", route: "/AffiliatedEntitiesA", section: "08", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Audit", route: "/AuditA", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Certification", route: "/CertificationA", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                            { title: "Reports", route: "/AfrReportsA", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Errors", route: "/ErrorsA", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        ]
                    }
                    break;
            }
        }

    } else {
        switch (tType) {
            case TaxonomyTypeEnum.COURT_REPORTING_939:
                initialLocation = "/CourtReporting939Admin";
                break;

            case TaxonomyTypeEnum.COURT_REPORTING_29:
                initialLocation = "/CR29Admin";
                break;

            default:
                initialLocation = "/AfrSummary";
                break;
        }
        if (selected && (selected.status === "REPORTED_BY_PRIMARY")) {
            if (selected.audittype === "AUDIT") {
                navOptions = [
                    { title: "Summary", route: "/AfrSummary", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                    { title: "Audit", route: "/Audit", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK }
                ]
            } else {
                navOptions = [
                    { title: "Summary", route: "/AfrSummary", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO }
                ]
            }
        } else {
            switch (tType) {
                case TaxonomyTypeEnum.COURT_REPORTING_939:
                    navOptions = [
                        { title: "Admin", route: "/CourtReporting939Admin", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        //                        { title: "Import 939 Data", route: "/CourtReporting939ImportExport", section: "20", conceptName: "" },
                        { title: "Court Innovations", route: "/CourtReporting939CI", section: enums.UISection_CR939.courtInnovations, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                        { title: "Legal Aid Program", route: "/CourtReporting939LAP", section: enums.UISection_CR939.legalAidProgram, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                        { title: "Law Library", route: "/CourtReporting939LL", section: enums.UISection_CR939.lawLibrary, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                        { title: "Juvenile Alternative Program", route: "/CourtReporting939JAP", section: enums.UISection_CR939.juvenileAlternativeProgram, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                        { title: "Summary", route: "/CourtReporting939Summary", section: enums.UISection_CR939.dataSummary, conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        { title: "Reports", route: "/CR939Reports", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        { title: "Errors", route: "/CR939Errors", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                    ]
                    break;

                case TaxonomyTypeEnum.COURT_REPORTING_29:

                    if (sectionCode == "99") {
                        navOptions = [
                            { title: "Admin", route: "/CR29Admin", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            // NOTE - Importing done in documents part of admin                        { title: "Import 939 Data", route: "/CourtReporting939ImportExportA", section: "20", conceptName: "" },
                            { title: "Part A", route: "/CR29DataA", section: enums.UISection_CR29.CR29PartA, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part B", route: "/CR29DataB", section: enums.UISection_CR29.CR29PartB, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part C", route: "/CR29DataC", section: enums.UISection_CR29.CR29PartC, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part D", route: "/CR29DataD", section: enums.UISection_CR29.CR29PartD, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part E", route: "/CR29DataE", section: enums.UISection_CR29.CR29PartE, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Certification", route: "/CR29Certification", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                            { title: "Reports", route: "/CR29Reports", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Errors", route: "/CR29Errors", section: "99", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Instructions", route: "/Instructions", section: enums.UISection_CR29.instructions, conceptName: "", optionType: NavOptionTypeEnum.INFO }
                        ]
                    }
                    else {
                        navOptions = [
                            { title: "Admin", route: "/CR29Admin", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            // NOTE - Importing done in documents part of admin                        { title: "Import 939 Data", route: "/CourtReporting939ImportExportA", section: "20", conceptName: "" },
                            { title: "Part A", route: "/CR29DataA", section: enums.UISection_CR29.CR29PartA, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part B", route: "/CR29DataB", section: enums.UISection_CR29.CR29PartB, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part C", route: "/CR29DataC", section: enums.UISection_CR29.CR29PartC, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part D", route: "/CR29DataD", section: enums.UISection_CR29.CR29PartD, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Part E", route: "/CR29DataE", section: enums.UISection_CR29.CR29PartE, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Certification", route: "/CR29Certification", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                            { title: "Reports", route: "/CR29Reports", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Errors", route: "/CR29Errors", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Instructions", route: "/Instructions", section: enums.UISection_CR29.instructions, conceptName: "", optionType: NavOptionTypeEnum.INFO }
                        ]
                    }
                    break;

                default:
                    var afrYear: number = selected ? selected.year : 2019
                    if (afrYear > 2023) {
                        if (mustReportDiscreteComponentUnits(selected)) {
                            navOptions = [
                                { title: "Summary", route: "/AfrSummary", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: "Import AFR Data", route: "/AfrDataImport", section: "02", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: utilityHelper.getFundGroupTitle("Governmental"), route: "/AccountsByGovernmental", section: enums.UISection_FundGroup.governmentalFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: utilityHelper.getFundGroupTitle("Proprietary"), route: "/AccountsByProprietary", section: enums.UISection_FundGroup.proprietaryFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: utilityHelper.getFundGroupTitle("Fiduciary"), route: "/AccountsByFiduciary", section: enums.UISection_FundGroup.fiduciaryFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: utilityHelper.getFundGroupTitle("Component Units"), route: "/AccountsByComponentUnits", section: enums.UISection_FundGroup.componentUnitsFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Long-Term Debt", route: "/LongTermDebt", section: "06", conceptName: "UAS_005_LongTermDebtTitle", optionType: NavOptionTypeEnum.WORK },
                                { title: "Supplemental Information", route: "/AfrData", section: "07", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Affiliated Entities", route: "/AffiliatedEntities", section: "08", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Audit", route: "/Audit", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Certification", route: "/Certification", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                                { title: "Reports", route: "/AfrReports", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: "Errors", route: "/Errors", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            ]
                        } else {
                            navOptions = [
                                { title: "Summary", route: "/AfrSummary", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: "Import AFR Data", route: "/AfrDataImport", section: "02", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: utilityHelper.getFundGroupTitle("Governmental"), route: "/AccountsByGovernmental", section: enums.UISection_FundGroup.governmentalFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: utilityHelper.getFundGroupTitle("Proprietary"), route: "/AccountsByProprietary", section: enums.UISection_FundGroup.proprietaryFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: utilityHelper.getFundGroupTitle("Fiduciary"), route: "/AccountsByFiduciary", section: enums.UISection_FundGroup.fiduciaryFunds, conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Long-Term Debt", route: "/LongTermDebt", section: "06", conceptName: "UAS_005_LongTermDebtTitle", optionType: NavOptionTypeEnum.WORK },
                                { title: "Supplemental Information", route: "/AfrData", section: "07", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Affiliated Entities", route: "/AffiliatedEntities", section: "08", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Audit", route: "/Audit", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                                { title: "Certification", route: "/Certification", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                                { title: "Reports", route: "/AfrReports", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                                { title: "Errors", route: "/Errors", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            ]
                        }
                    } else if (selected && selected.year > 2022) {
                        navOptions = [
                            { title: "Summary", route: "/AfrSummary", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Import AFR Data", route: "/AfrDataImport", section: "02", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Balance Sheet", route: "/BalanceSheet", section: "03", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Revenues", route: "/Revenues", section: "04", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Expenditures", route: "/Expenditures", section: "05", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Long-Term Debt", route: "/LongTermDebt", section: "06", conceptName: "UAS_005_LongTermDebtTitle", optionType: NavOptionTypeEnum.WORK },
                            { title: "Supplemental Information", route: "/AfrData", section: "07", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Affiliated Entities", route: "/AffiliatedEntities", section: "08", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Audit", route: "/Audit", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Certification", route: "/Certification", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                            { title: "Reports", route: "/AfrReports", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Errors", route: "/Errors", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        ]
                    } else if (selected && selected.year > 2021) {
                        navOptions = [
                            { title: "Summary", route: "/AfrSummary", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Import AFR Data", route: "/AfrDataImport", section: "02", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Balance Sheet", route: "/BalanceSheet", section: "03", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Revenues", route: "/Revenues", section: "04", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Expenditures", route: "/Expenditures", section: "05", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Long-Term Debt", route: "/LongTermDebt", section: "06", conceptName: "UAS_005_LongTermDebtTitle", optionType: NavOptionTypeEnum.WORK },
                            { title: "Supplemental Information", route: "/AfrData", section: "07", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Affiliated Entities", route: "/AffiliatedEntities", section: "08", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Audit", route: "/Audit", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Certification", route: "/Certification", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                            { title: "Reports", route: "/AfrReports", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Errors", route: "/Errors", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        ]
                    } else if (afrYear > 2020) {
                        navOptions = [
                            { title: "Summary", route: "/AfrSummary", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Import AFR Data", route: "/AfrDataImport", section: "02", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Revenues", route: "/Revenues", section: "04", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Expenditures", route: "/Expenditures", section: "05", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Long-Term Debt", route: "/LongTermDebt", section: "06", conceptName: "UAS_005_LongTermDebtTitle", optionType: NavOptionTypeEnum.WORK },
                            { title: "Supplemental Information", route: "/AfrData", section: "07", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Affiliated Entities", route: "/AffiliatedEntities", section: "08", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Audit", route: "/Audit", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Certification", route: "/Certification", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                            { title: "Reports", route: "/AfrReports", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Errors", route: "/Errors", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        ]
                    } else {
                        navOptions = [
                            { title: "Summary", route: "/AfrSummary", section: "01", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Import AFR Data", route: "/AfrDataImport", section: "02", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Revenues", route: "/Revenues", section: "04", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Expenditures", route: "/Expenditures", section: "05", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Long-Term Debt", route: "/LongTermDebt", section: "06", conceptName: "UAS_005_LongTermDebtTitle", optionType: NavOptionTypeEnum.WORK },
                            { title: "Affiliated Entities", route: "/AffiliatedEntities", section: "08", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Audit", route: "/Audit", section: "09", conceptName: "", optionType: NavOptionTypeEnum.WORK },
                            { title: "Certification", route: "/Certification", section: "10", conceptName: "", optionType: NavOptionTypeEnum.SUBMIT },
                            { title: "Reports", route: "/AfrReports", section: "11", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                            { title: "Errors", route: "/Errors", section: "12", conceptName: "", optionType: NavOptionTypeEnum.INFO },
                        ]
                    }
                    break;
            }
        }
    }
    const auditMessage: AuditMessage = useAppSelector(isAFRAuditRequired);
    const [navState, setNavState] = useState<NavOptionGroup[]>(utilityHelper.generateNavGroups(tType, navOptions, user, selected, selectedEntity, auditMessage, dispatch, errorList, rowErrorList));
    //    alert(selectedId);
    const [values, setValues] = useState({
        selectedOption: initialLocation,
        selectedVersion: selectedId
    });
    //    alert(values.selectedOption);
    if (values.selectedOption !== location.pathname) {
        setValues({ ...values, selectedOption: location.pathname });
    }
    //TODO change data binding to use entities slice
    const onChangeValue = (event: any) => {
        setValues({ ...values, selectedOption: event.target.value });
        dispatch(resetConceptState({}));
        history.push(event.target.value);
    }
    const showUploadSuccessful = function (currentAfr: Afr | null) {
        let rname = utilityHelper.getReportTypeName(currentAfr);
        dispatch(setIsLoading(false));
        Swal.fire(
            "Save " + rname,
            rname + " saved successfully." + "<hr />" + "Your " + rname + " has not been submitted.",
            'info'
        );
    }

    const showSubmitSuccessful = function (currentAfr: Afr | null) {
        let rname = utilityHelper.getReportTypeName(currentAfr);
        dispatch(setIsLoading(false));
        Swal.fire(
            "Submit " + rname,
            rname + " submitted successfully.",
            'success'
        );
    }

    const showSubmitSuccessfulWithSurvey = function (currentAfr: Afr | null) {
        let rname = utilityHelper.getReportTypeName(currentAfr);
        dispatch(setIsLoading(false));
        Swal.fire(
            {
                icon: 'success',
                title: "Submit " + rname,
                html: "<p>" + rname + " submitted successfully.</p><p><b>Would you like to complete a brief survey?</b></p>",
                allowOutsideClick: false,
                showCancelButton: true,
                confirmButtonColor: '#28a745',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes!',
                cancelButtonText: 'No & Close',
                reverseButtons: true
            }
        ).then((result: any) => {
            if (result.isConfirmed) {
                setModalSurveyIsOpen(true);
            } /* No and Close */
            else {
                handleSubmitOrDecline(true, {});
            }

        });

    }
    const showSubmitHasErrors = function (currentAfr: Afr | null) {
        let rname = utilityHelper.getReportTypeName(currentAfr);
        dispatch(setIsLoading(false));
        Swal.fire(
            "Oops!",
            "The submitted report has errors. Please see the Errors Section or AFR Assistance window for details.",
            'error'
        );
    }

    const showSubmitHasValidationError = function (currentAfr: Afr | null, errMsg: string) {
        if (!currentAfr || !currentAfr.currentversion) {
            return showSubmitHasErrors(currentAfr);
        }
        let rname = utilityHelper.getReportTypeName(currentAfr);
        dispatch(setIsLoading(false));
        Swal.fire(
            "Oops!",
            errMsg,
            'error'
        );
    }

    function adminNotifySwalFire(notifyResult: any, dispatch: any, selectedId: string, tType: TaxonomyTypeEnum | null, userRole: any, history: any) {
        dispatch(setIsLoading(false));
        Swal.fire({
            title: notifyResult.subject,
            html: notifyResult.message,
            icon: 'warning',

        })

        // rollback changes
        dispatch(loadAfr(selectedId));
        dispatch(loadEvents(selectedId));
        dispatch(resetConceptState({}));
        //reset the dirty flag so the save/undo buttons disappear
        dispatch(setIsDirty({ IsDirty: false, Page: "" }));

        switch (tType) {
            case TaxonomyTypeEnum.COURT_REPORTING_939:
                if (userRole === "ADMIN") {
                    history.push("/CourtReporting939AdminA");
                } else {
                    history.push("/CourtReporting939Admin");
                }
                break;

            case TaxonomyTypeEnum.COURT_REPORTING_29:
                if (userRole === "ADMIN") {
                    history.push("/CR29AdminA");
                } else {
                    history.push("/CR29Admin");
                }
                break;

            default:
                if (userRole === "ADMIN") {
                    history.push("/AfrSummaryA");
                } else {
                    history.push("/AfrSummary");
                }
                break;
        }
    }


    function lgeNotifySwalFire(notifyResult: any, dispatch: any, selectedId: string, tType: TaxonomyTypeEnum | null, userRole: any, history: any) {
        dispatch(setIsLoading(false));
        Swal.fire({
            title: notifyResult.subject,
            html: notifyResult.message,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, discard changes!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        }).then((result: any) => {
            if (result.isConfirmed) {

                // rollback changes
                dispatch(loadAfr(selectedId));
                dispatch(loadEvents(selectedId));
                dispatch(resetConceptState({}));
                //reset the dirty flag so the save/undo buttons disappear
                dispatch(setIsDirty({ IsDirty: false, Page: "" }));
                switch (tType) {
                    case TaxonomyTypeEnum.COURT_REPORTING_939:
                        if (userRole === "ADMIN") {
                            history.push("/CourtReporting939AdminA");
                        } else {
                            history.push("/CourtReporting939Admin");
                        }
                        break;

                    case TaxonomyTypeEnum.COURT_REPORTING_29:
                        if (userRole === "ADMIN") {
                            history.push("/CR29AdminA");
                        } else {
                            history.push("/CR29Admin");
                        }
                        break;

                    default:
                        if (userRole === "ADMIN") {
                            history.push("/AfrSummaryA");
                        } else {
                            history.push("/AfrSummary");
                        }
                        break;
                }
            }
        });
    }

    const handleSaveOrSubmitClick = (submitOnSave: boolean) => {
        dispatch(setIsLoading(true));

      
            //check if AFR is out of sync      
            if (selected) {

                var entityIdValue = selectedEntity ? selectedEntity.code : "";
                var afrIdValue = selected.afrId;
                var versionNumberValue = selected.currentversion.versionNumber;
                var taxonomyTypeValue = selected.taxonomyType ? selected.taxonomyType.toString() : "";

                var afrVersionDetail: { entityId: string, afrId: string, versionNumber: number, taxononmyType: string }
                    = {
                    entityId: entityIdValue,
                    afrId: afrIdValue,
                    versionNumber: versionNumberValue,
                    taxononmyType: taxonomyTypeValue
                }

                dispatch(getReportNotify(afrVersionDetail)).then((result: any) => {

                    const notifyResult = unwrapResult(result);
                    if (notifyResult !== null) {

                        if (userRole === "ADMIN") {
                            adminNotifySwalFire(notifyResult, dispatch, selectedId, tType, userRole, history);
                        } else {
                            lgeNotifySwalFire(notifyResult, dispatch, selectedId, tType, userRole, history);
                        }
                    }
                    else {

                        // Saving pending changes to AfrSlice
                        utilityHelper.saveDeferredChanges(dispatch);

                        /* get the most updated Afr reference */
                        const currentAFR: Afr = dispatch(getSelector(getSelectedAfr));
                        if (!isNotThirdParty && submitOnSave === true) {
                            dispatch(thirdPartyNotifyLGEs(currentAFR.afrId))
                                .then((result: any) => {
                                    dispatch(setIsLoading(false));
                                    if (result.payload != null && result.payload == "Notification Email(s) Sent") {
                                        Swal.fire('Notification Sent', '', 'info');
                                    }
                                    else {
                                        Swal.fire('Oops...', 'Something went wrong!', 'error')
                                    }
                                });
                            return;
                        }

                        var commitCall = {
                            currentAfr: currentAFR,
                            changedVersionStates: stateChanges,
                            submit: submitOnSave
                        }
                        dispatch(commitAfr(commitCall)).then((result: any) => {
                            dispatch(resetConceptState({}));
                            if (result.payload.status === "ERROR") {
                                var errMsg: string[] = [];
                                result.payload.currentversion.validationerrors.map((x: ValidationError) => {
                                    errMsg.push(x.message);
                                });
                                showSubmitHasValidationError(currentAFR, errMsg.join(";"));
                                return;
                            }
                            //reset the dirty flag
                            dispatch(setIsDirty({ IsDirty: false, Page: "" }));
                            if (submitOnSave) {
                                if (result.payload.status === "IN_PROGRESS") {
                                    showSubmitHasErrors(currentAFR);
                                } else {
                                    showSubmitSuccessfulWithSurvey(currentAFR);
                                }
                            } else {
                                showUploadSuccessful(currentAFR);
                            }

                        });
                    }
                });
            }

        
       
    }

    const handleSaveClick = () => {
        handleSaveOrSubmitClick(false);
    }

    const handleSubmitClick = () => {
        handleSaveOrSubmitClick(true);
    }

    const useSubmit = (currentAFR: Afr) => {
        var submitOnSave = false;
        if (currentAFR) {
            switch (currentAFR.taxonomyType) {
                case TaxonomyTypeEnum.COURT_REPORTING_939:
                    if (totalErrorCount < 1 && currentAFR.currentversion && currentAFR.currentversion.tables.some(x => x.rows.some(y => y.facts.length > 0))) {
                        if (utilityHelper.isAfrAtLeastSubmitted(currentAFR) == false) {
                            submitOnSave = true;
                        }
                    }
                    break;

                case TaxonomyTypeEnum.COURT_REPORTING_29:
                    submitOnSave = false;
                    break;

                default:
                    submitOnSave = false;
                    break;
            }
        }
        return submitOnSave;
    }
    const showSubmit = (currentAFR: Afr) => {
        return true;
    }
    const useSave = (currentAFR: Afr) => {
        if (!isDirtyInfo) {
            return false;
        }
        var useSaveBtn = true;
        if (currentAFR) {
            switch (currentAFR.taxonomyType) {
                case TaxonomyTypeEnum.COURT_REPORTING_939:
                case TaxonomyTypeEnum.COURT_REPORTING_29:
                    //                    if (!errorList || errorList.length === 0) {
                    useSaveBtn = true;
                    //                    }
                    break;

                default:
                    useSaveBtn = true;
                    break;
            }
        }
        return useSaveBtn;
    }

    const handleUndoClick = () => {
        if (selected) {
            dispatch(setIsLoading(false));
            Swal.fire({
                title: '<b>Are you sure?</b>',
                html: "There are unsaved changes to your AFR. Do you want to <b>discard</b> your changes? <br/> You won't be able to revert this!",
                icon: 'warning',
                allowOutsideClick: false,
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonColor: '#3085d6',
                denyButtonColor: '#28a745',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, discard it!',
                cancelButtonText: 'No, cancel!',
                denyButtonText: 'Save and Close!',
                reverseButtons: true
            }).then((result: any) => {
                if (result.isConfirmed) {

                    // rollback changes
                    dispatch(loadAfr(selectedId));
                    dispatch(loadEvents(selectedId));
                    dispatch(resetConceptState({}));
                    //reset the dirty flag so the save/undo buttons disappear
                    dispatch(setIsDirty({ IsDirty: false, Page: "" }));
                    switch (tType) {
                        case TaxonomyTypeEnum.COURT_REPORTING_939:
                            if (userRole === "ADMIN") {
                                history.push("/CourtReporting939AdminA");
                            } else {
                                history.push("/CourtReporting939Admin");
                            }
                            break;

                        case TaxonomyTypeEnum.COURT_REPORTING_29:
                            if (userRole === "ADMIN") {
                                history.push("/CR29AdminA");
                            } else {
                                history.push("/CR29Admin");
                            }
                            break;

                        default:
                            if (userRole === "ADMIN") {
                                history.push("/AfrSummaryA");
                            } else {
                                history.push("/AfrSummary");
                            }
                            break;
                    }

                } /* Save and Close */
                else if (result.isDenied) {

                    var commitCall = {
                        currentAfr: selected,
                        changedVersionStates: stateChanges,
                        submit: false
                    }
                    dispatch(commitAfr(commitCall));
                    dispatch(resetConceptState({}));
                    //reset the dirty flag
                    dispatch(setIsDirty({ IsDirty: false, Page: "" }));
                    dispatch(setIsLoading(false));
                    Swal.fire('Changes saved!', '', 'info');
                }

            });

        }
    }

    const shouldDisableSubmit = function (): boolean {
        if (!selected) {
            return true;
        }

        if ((selected.status === enums.AFRStatus.in_progress || selected.status === enums.AFRStatus.returned_by_dfs  ) &&  (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin))
        {
            return true;
        }

        if (utilityHelper.checkStatusList(selected, enums.verifiedCertifiedRecertified)
            || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin)) {
            return true;
        }

        if (selected.taxonomyType === "COURT_REPORTING_29") {
            var factsValueFound = utilityHelper.getCR29FactsAndDocuments(selected);
            if (factsValueFound === "") {
                return true;
            }
        }

        switch (selected.taxonomyType) {
            case TaxonomyTypeEnum.COURT_REPORTING_939:
                // NOTE - for 939 we don't pay attention to the dirty flag, since there's no interactive editing allowed, so if we haven't certified (see clause
                // above) then the only thing that matters is whether the current state is error free. For example, we might have save a previously good state
                // and loaded the report, so that all we want to do is submit the report - this should be allowed                                                                                                      
                if (totalErrorCount < 1 && selected.currentversion && selected.currentversion.tables.some(x => x.rows.some(y => y.facts.length > 0))) {
                    if (utilityHelper.isAfrAtLeastSubmitted(selected) == false) {
                        return false;
                    }
                }
                return true;

            case TaxonomyTypeEnum.COURT_REPORTING_29:
                if (isDirtyInfo) {
                    return true;
                }
                return !(utilityHelper.evaluateAllSectionOK(loggedUser, selected, selectedEntity, errorList, auditMessage, dispatch))

            default:
                if (isDirtyInfo) {
                    return true;
                }
                return !(utilityHelper.evaluateAllSectionOK(loggedUser, selected, selectedEntity, errorList, auditMessage, dispatch))
        }
    }
    const afrChanged = function () {

        if (selected) {
            return selected.afrId;
        }
        return null;
    }
    const afrSectionsDoneChanged = function () {
        if (selected) {
            return isFundGroupOriented(selected) ? utilityHelper.getDoneStateByFundGroups(selected) : utilityHelper.getDoneStateByAccountGroups(selected);
        }
        return "";
    }

    const paintMenuSection = function (idx: number, group: string, year: any, section: any, route: any, title: any, value: string) {

        var render: boolean;
        const jsxSection =
            <div>
                <Row key={route}>
                    <Col className="col-lg-3 text-right">
                        <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                            type="grow" color="warning"
                            hidden={selectedLoadAfrStatus != "loadingAfr"} />
                        <div hidden={selectedLoadAfrStatus == "loadingAfr"}>
                            <NavigatorIconNotification page={route} section={section} />
                        </div>
                    </Col>

                    <Col className="col-lg-6 text-left">

                        <Input key={value}
                            type={selectedLoadAfrStatus == "loadingAfr" ? "hidden" : "radio"}
                            value={value}
                            name="afrnavigator"
                            section={section}
                            onChange={onChangeValue}
                            checked={value === values.selectedOption}

                        />
                        {title}
                    </Col>
                </Row>
            </div>;


        render = utilityHelper.renderAFRMenuOption(selected, selectedEntity, section);

        if (!render) {
            utilityHelper.removeErrorsInSection(errorList, section, dispatch)
        }

        return render && jsxSection
    }

    // Survey support
    const getNumberAsText = function (value: number | null): string {
        return value ? value + "" : "";
    }
    const createSurveyParts = function (refId: string, afr: Afr, declined: boolean, questions: any, proto: Reference): PartInput[] {
        var results: PartInput[] = [];

        proto.parts.map((part: Part) => {
            var pdName = part.definition.name;
            var pdValue = "";
            switch (pdName) {
                case "UASID":
                    pdValue = refId;
                    break;

                case "TaxonomyType":
                    pdValue = afr.taxonomyType ? afr.taxonomyType : "AFR";
                    break;

                case "FiscalYear":
                    pdValue = afr.year + "";
                    break;

                case "Quarter":
                    pdValue = afr.logicalPeriod ? afr.logicalPeriod : "ANNUAL";
                    break;

                case "Revision":
                    pdValue = afr.currentversion ? getNumberAsText(afr.currentversion.revisionNumber) : "";
                    break;

                case "Amendment":
                    pdValue = afr.currentversion ? getNumberAsText(afr.currentversion.amendmentNumber) : "";
                    break;

                case "UserName":
                    pdValue = loggedUser.login;
                    break;

                case "Declined":
                    pdValue = declined ? "true" : "false";
                    break;

                default:
                    if (questions.hasOwnProperty(pdName)) {
                        pdValue = questions[pdName];
                    }
                    break;
            }
            results.push({ partName: pdName, partValue: pdValue });
        });
        return results;
    }
    const handleSubmitOrDecline = function (decline: boolean, questions: any) {
        if (selected && selectedEntity && protoSurvey && loggedUser) {
            var refId: string = utilityHelper.generateGuid()
            var surveyRefAssignment: ReferenceAssignmentInput = {
                referenceId: refId,
                conceptId: "RepUnitCode_" + selectedEntity.code,
                remove: false
            }
            var refParts: Part
            var surveyRef: ReferenceInput = {
                referenceType: protoSurvey.type,
                parts: createSurveyParts(refId, selected, decline, questions, protoSurvey)
            }
            // NOTE - We're updating the base taxonomy for now. All survey results will be attached to the based where the entity member references reside
            var surveyResults: TaxonomyUpdate = {
                year: 0,
                references: [surveyRef],
                referenceAssignments: [surveyRefAssignment],
                partDefinitions: [],
                labels: [],
                customRoles: [],
                deprecations: [],
                relationships: [],
                concepts: [],
                accountScopes: [],
                accountDependencies: []
            }
            dispatch(immediateUpdate(surveyResults)).then((response: any) => {
                var report: TaxonomyUpdateReport | null = response.payload;
                if (!report) {
                    alert("no response");
                }

            });
        }
    }

    // reset the error list each time the afr is change
    React.useEffect(function () {
        // We should not need to reset the validation state here. The loading of a different Afr will trigger re-validation anyway, and all we need to do here is update the nav groupings
        // based on the state of the sections, and now including any errors.
        //dispatch(resetValidationState({}));
        setNavState(utilityHelper.generateNavGroups(tType, navOptions, user, selected, selectedEntity, auditMessage, dispatch, errorList, rowErrorList));

    }, [afrChanged(), afrSectionsDoneChanged()]);

    React.useEffect(function () {
        // This useEffect will generate a possibly new nav grouping based on any changes detected in the error list. At present the client does not have all the validations that are present 
        // in the server, so to ensure the most complete validations users need to save the work in progress (in which case the useEffect above will trigger)
        setNavState(utilityHelper.generateNavGroups(tType, navOptions, user, selected, selectedEntity, auditMessage, dispatch, errorList, rowErrorList));
    }, [errorList, rowErrorList]);

    React.useEffect(function () {
        var bNotThirdParty = true;
        if (!isAdminUser) {
            if (selectedEntity && authorizedEntitiesSelector) {

                var foundEntityAuthorization = authorizedEntitiesSelector.find((element) => element.entity.code == selectedEntity.code);

                if (foundEntityAuthorization && foundEntityAuthorization.entityUserRole == "THIRD_PARTY") {
                    bNotThirdParty = false;
                }
            }
        }
        setIsNotThirdParty(bNotThirdParty);

        if (selected) {
            var vRef: VersionRef = getTaxonomyReferenceForAfr(selected);
            if (vRef.type === TaxonomyTypeEnum.AFR) {
                var vInfo: ActiveTaxonomyVersionInfo = {
                    type: vRef.type,
                    year: vRef.year,
                    periods: [],
                    isPublished: true,
                    id: vRef.type + vRef.year
                }
                dispatch(loadScopeNetworks(vInfo));
                dispatch(loadTaxonomyReference("survey")).then((result: any) => setProtoSurvey(result.payload));
            } else {
                dispatch(resetNetworkState);
                setProtoSurvey(null);
            }

            const filter: NetworkConceptsFilter = {
                taxonomyVersion: vRef,
                nameFilter: "*Title"
            }
            dispatch(loadTitles(filter));
            switch (tType) {
                case TaxonomyTypeEnum.AFR:
                    setSubmitButtonTitle("Submit AFR");
                    break;

                case TaxonomyTypeEnum.COURT_REPORTING_939:
                    setSubmitButtonTitle("Submit");
                    var vInfo: ActiveTaxonomyVersionInfo = {
                        type: tType,
                        periods: [],
                        id: "",
                        year: selected ? selected.year : 2022,
                        isPublished: true
                    }
                    dispatch(loadDefinitionNetworks(vInfo)).then((networksPayload: any) => {
                        var dimNetwork: Network[] = networksPayload.payload.filter((x: any) => x.name === "classes-of-939-court-reporting");
                        if (dimNetwork && dimNetwork.length > 0) {
                            dispatch(setDimensionSectionMap(dimNetwork[0]));
                        }
                    });
                    break;

                default:
                    setSubmitButtonTitle("Submit");
                    break;
            }
            setProtoSurvey(null);
        } else {
            dispatch(resetTitleState());
            setSubmitButtonTitle("Submit");
            setProtoSurvey(null);
        }
        if (!bNotThirdParty) {
            setSubmitButtonTitle("Send Notification");
        }
        setNavState(utilityHelper.generateNavGroups(tType, navOptions, user, selected, selectedEntity, auditMessage, dispatch, errorList, rowErrorList));
    }, [selected]);


    return (
        <React.Fragment>


            {
                selected ?
                    (<div onChange={onChangeValue}>
                        <div style={{ maxWidth: "fit-content", marginInline: "auto" }} >
                            {useSave(selected)
                                && <Button type="button" style={{ backgroundColor: "green", marginRight: "1em" }} onClick={handleSaveClick}>Save</Button>}
                            {isDirtyInfo
                                && <Button type="button" style={{ backgroundColor: "red" }} onClick={handleUndoClick}>Undo</Button>}
                        </div>
                        <React.Fragment>
                            {navState.map((ns, idx) => (
                                <div key={ns.group}>
                                    <div style={{ marginBottom: "1em" }}>
                                        <div>
                                            <Row>
                                                <Col className="col-lg-3 text-right"></Col>
                                                <Col className="col-lg-6 text-left">
                                                    <Label><h5 style={{ textDecoration: "underline" }}>{ns.group}</h5></Label>
                                                </Col>
                                            </Row>
                                        </div>
                                        {ns.options.map((optState, idxOption) => (
                                            paintMenuSection(idxOption, ns.group, selected.year, optState.option.section, optState.option.route, optState.option.title, optState.option.route)
                                        ))}
                                    </div>
                                </div>

                            ))}
                        </React.Fragment>
                        {showSubmit(selected) &&
                            <div style={{ maxWidth: "fit-content", marginInline: "auto" }} >
                                {shouldDisableSubmit()
                                    ?
                                    <Button size="lg" type="button" style={{ backgroundColor: "gray", fontSize: "1.75rem" }} disabled={true}>{submitButtonTitle}</Button>
                                    :
                                    <Button size="lg" type="button" style={{ backgroundColor: "green", fontSize: "1.75rem" }} onClick={handleSubmitClick} >{submitButtonTitle}</Button>
                                }

                            </div>
                        }

                    </div>)
                    :
                    (<div></div>)

            }
            {selected && selectedEntity && protoSurvey && (<AfrSubmissionSurvey
                entity={selectedEntity}
                afr={selected}
                proto={protoSurvey}
                isOpen={modalSurveyIsOpen}
                onCloseModal={handleCloseSurveyModal}
                handleSubmitOrDecline={handleSubmitOrDecline}
            />)}
        </React.Fragment>
    )
}

export default AfrNavigator;
